import React, { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, Pencil, Package, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, brl, errMsg } from "@/lib/api";

const FIELDS = [
  ["sku", "SKU"], ["ean", "EAN"], ["descricao", "Descrição"], ["categoria", "Categoria"],
  ["marca", "Marca"], ["unidade", "Unidade"], ["custo", "Custo", "number"], ["preco", "Preço", "number"],
  ["estoque", "Estoque", "number"], ["estoque_minimo", "Estoque mínimo", "number"],
  ["ncm", "NCM"], ["cest", "CEST"], ["cfop", "CFOP"],
];

export default function Produtos() {
  const [list, setList] = useState(null);
  const [modal, setModal] = useState(null); // null | {} | product

  const load = () => api.get("/products").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...modal };
      ["custo", "preco", "estoque", "estoque_minimo"].forEach((f) => { if (body[f] !== undefined && body[f] !== "") body[f] = parseFloat(body[f]); });
      if (modal.id) await api.put(`/products/${modal.id}`, body);
      else await api.post("/products", body);
      toast.success("Produto salvo");
      setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => { try { await api.delete(`/products/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!list) return <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>;

  return (
    <div className="animate-fade-up">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Catálogo de Produtos</h1>
          <p className="text-sm text-slate-500">{list.length} produtos cadastrados</p>
        </div>
        <button data-testid="add-product-btn" onClick={() => setModal({})} className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">
          <Plus className="h-4 w-4" /> Novo produto
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
            <tr>
              <th className="p-3">SKU</th><th className="p-3">Descrição</th><th className="p-3">Categoria</th>
              <th className="p-3 text-right">Custo</th><th className="p-3 text-right">Preço</th>
              <th className="p-3 text-right">Estoque</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((p) => (
              <tr key={p.id} data-testid="product-row" className="hover:bg-slate-50">
                <td className="p-3 font-mono text-xs text-slate-600">{p.sku}</td>
                <td className="p-3 font-medium text-slate-800">{p.descricao}</td>
                <td className="p-3"><span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{p.categoria}</span></td>
                <td className="p-3 text-right font-mono">{brl(p.custo)}</td>
                <td className="p-3 text-right font-mono font-semibold">{brl(p.preco)}</td>
                <td className="p-3 text-right">
                  <span className={`font-mono ${p.estoque <= p.estoque_minimo ? "font-bold text-rose-600" : "text-slate-700"}`}>{p.estoque}</span>
                  {p.estoque <= p.estoque_minimo && <AlertTriangle className="ml-1 inline h-3.5 w-3.5 text-rose-500" />}
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setModal(p)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-sky-600"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(p.id)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(null)} />
          <div className="relative w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center gap-2"><Package className="h-5 w-5 text-sky-600" /><h3 className="font-display text-lg font-bold">{modal.id ? "Editar produto" : "Novo produto"}</h3></div>
            <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto">
              {FIELDS.map(([k, label, type]) => (
                <div key={k} className={k === "descricao" ? "col-span-2" : ""}>
                  <label className="mb-1 block text-xs font-semibold text-slate-600">{label}</label>
                  <input data-testid={`product-input-${k}`} type={type || "text"} value={modal[k] ?? ""} onChange={(e) => setModal({ ...modal, [k]: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" />
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
              <button data-testid="product-save-btn" onClick={save} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
